import { useState } from 'react';
import { Server, Monitor, Wifi, HardDrive, Printer, Shield, Router, Network, Box, Plus, Key, Cctv } from 'lucide-react';
import AddAssetModal from '../Assets/AddAssetModal';
import AddCredentialModal from '../Assets/AddCredentialModal'; // Import the credential modal
import { hasPermission } from '../../utils/permissionsHelper';
import SwitchGroupModal from './SwitchGroupModal';

interface Asset {
    id: number;
    name: string;
    asset_type: string;
    hostname: string;
    ip_address: string;
    mac_address: string;
    client_id: number;
    status: string;
    total_ports?: number | null;
}

interface AssetsPaletteProps {
    assets: Asset[];
    loading: boolean;
    onAssetAdded?: () => void;
    usedAssetIds?: Set<number>; // New Prop for disabling used assets
    callTopology: () => void;
}

const getAssetIcon = (type: string) => {
    const icons: Record<string, any> = {
        camera: Cctv,
        server: Server,
        workstation: Monitor,
        access_point: Wifi,
        nvr: HardDrive,
        printer: Printer,
        firewall: Shield,
        router: Router,
        switch: Router,
        storage: HardDrive,
        local_asset: Server,
        isp: Wifi,
        other: Box,
    };
    return icons[type] || Box;
};

export default function AssetsPalette({ assets = [], loading, onAssetAdded, usedAssetIds = new Set(), callTopology }: AssetsPaletteProps) {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSwitchGroupModal, setShowSwitchGroupModal] = useState(false);

    // Credential Modal State
    const [credentialModalOpen, setCredentialModalOpen] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
    const [credentialClientId, setCredentialClientId] = useState<any>(null);
     const canCreate = hasPermission('assets', 'create')

    const onDragStart = (event: React.DragEvent, asset: Asset) => {
        // Prevent dragging if asset is already on canvas
        if (usedAssetIds.has(asset.id)) {
            event.preventDefault();
            return;
        }

        const nodeType = asset.asset_type === 'switch' ? 'switchNode' : 'assetNode';
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.setData('assetType', asset.asset_type);
        event.dataTransfer.setData('assetName', asset.name);
        event.dataTransfer.setData('assetId', `asset-${asset.id}`);
        event.dataTransfer.setData('assetIp', asset.ip_address);
        event.dataTransfer.setData('totalPorts', (asset.total_ports || 24).toString());
        event.dataTransfer.effectAllowed = 'move';
    };

    const handleCredentialClick = (e: React.MouseEvent, assetId: number, clientId: number) => {
        e.stopPropagation(); // Prevent drag start when clicking the button
        e.preventDefault();
        setSelectedAssetId(assetId);
        setCredentialClientId(clientId);
        setCredentialModalOpen(true);
    };

    const safeAssets = Array.isArray(assets) ? assets : [];
    const groupedAssets = safeAssets.reduce((acc, asset) => {
        const type = asset.asset_type || 'other';
        if (!acc[type]) acc[type] = [];
        acc[type].push(asset);
        return acc;
    }, {} as Record<string, Asset[]>);

    return (
        <>
            <div className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col h-full select-none">
                {/* Header */}
                <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between bg-gray-900/50 backdrop-blur-sm sticky top-0 z-10">
                    <div className="flex items-center gap-2">
                        <Network className="w-4 h-4 text-orange-500" />
                        <span className="text-xs font-bold text-gray-200 uppercase tracking-wide">Assets</span>
                    </div>
                    {!loading && (
                        <span className="text-[10px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded-full border border-gray-700">
                            {safeAssets.length}
                        </span>
                    )}
                </div>

                {/* Create Asset Button */}
                {canCreate && (
                <div className="p-2 border-b border-gray-800 flex flex-col gap-2">
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="w-full flex items-center justify-center gap-2 py-1.5 bg-gray-800 hover:bg-gray-700 text-orange-400 hover:text-orange-300 text-xs font-semibold rounded-md border border-gray-700 hover:border-gray-600 transition-all duration-200"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Create Asset
                    </button>
                    <button
                        onClick={() => setShowSwitchGroupModal(true)}
                        className="w-full flex items-center justify-center gap-2 py-1.5 bg-gray-800 hover:bg-gray-700 text-orange-400 hover:text-orange-300 text-xs font-semibold rounded-md border border-gray-700 hover:border-gray-600 transition-all duration-200"
                    >
                        <Network className="w-3.5 h-3.5" />
                        Group Switch
                    </button>
                </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-2 space-y-3">
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : safeAssets.length === 0 ? (
                        <div className="text-center py-8 opacity-50">
                            <p className="text-[10px] text-gray-500">No assets found</p>
                        </div>
                    ) : (
                        Object.entries(groupedAssets).map(([type, typeAssets]) => {
                            const Icon = getAssetIcon(type);
                            return (
                                <div key={type} className="space-y-1">
                                    <div className="flex items-center justify-between px-1 text-[10px] font-semibold text-gray-200 uppercase tracking-wider mb-1">
                                        <div className="flex items-center gap-1.5">
                                            <Icon className="w-3 h-3" />
                                            {type.replace(/_/g, ' ')}
                                        </div>
                                        <span>{typeAssets.length}</span>
                                    </div>

                                    <div className="grid gap-1">
                                        {typeAssets.map((asset) => {
                                            const AssetIcon = getAssetIcon(asset.asset_type);
                                            const isUsed = usedAssetIds.has(asset.id);

                                            return (
                                                <div
                                                    key={asset.id}
                                                    draggable={!isUsed}
                                                    onDragStart={(e) => onDragStart(e, asset)}
                                                    className={`group flex items-center gap-2 p-1.5 border rounded relative overflow-hidden pr-8 transition-all
                                                        ${isUsed
                                                            ? 'bg-gray-800/20 border-gray-800 opacity-40 cursor-not-allowed grayscale'
                                                            : 'bg-gray-800/40 hover:bg-gray-800 border-gray-800 hover:border-gray-700 cursor-grab active:cursor-grabbing'
                                                        }`}
                                                >
                                                    {/* Status Dot */}
                                                    <div
                                                        className={`w-1.5 h-1.5 rounded-full ml-1 flex-shrink-0 ${asset.status === 'active' ? 'bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.4)]' :
                                                            asset.status === 'inactive' ? 'bg-gray-600' :
                                                                asset.status === 'maintenance' ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                    />

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className={`text-[11px] font-medium truncate ${isUsed ? 'text-gray-500' : 'text-gray-300 group-hover:text-white'}`}>
                                                                {asset.name}
                                                            </span>
                                                            <AssetIcon className={`w-3 h-3 ${isUsed ? 'text-gray-600' : 'text-gray-500 group-hover:text-blue-400'}`} />
                                                        </div>

                                                        <div className="flex items-center justify-between mt-0.5">
                                                            <span className={`text-[9px] font-mono truncate ${isUsed ? 'text-gray-600' : 'text-gray-600 group-hover:text-gray-500'}`}>
                                                                {asset.ip_address}
                                                            </span>
                                                            {asset.asset_type === 'switch' && (
                                                                <span className="text-[9px] text-gray-500">
                                                                    {asset.total_ports || 24}P
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Used Badge or Key Button */}
                                                    {isUsed ? (
                                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-gray-600 bg-gray-900 px-1 rounded">
                                                            USED
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => handleCredentialClick(e, asset.id, asset.client_id)}
                                                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-yellow-400 hover:bg-gray-700 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                                            title="Manage Credentials"
                                                        >
                                                            <Key className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Add Asset Modal */}
            <AddAssetModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={() => {
                    if (onAssetAdded) onAssetAdded();
                }}
            />

            {/* Credential Modal */}
            <AddCredentialModal
                open={credentialModalOpen}
                assetId={selectedAssetId}
                clientId={credentialClientId}
                onClose={() => {
                    setCredentialModalOpen(false);
                    setSelectedAssetId(null);
                    callTopology();
                    if (onAssetAdded) onAssetAdded();
                }} />

            {/* Switch Group Modal */}
            <SwitchGroupModal
                isOpen={showSwitchGroupModal}
                onClose={() => setShowSwitchGroupModal(false)}
            />
        </>
    );
}
